declare const self: ServiceWorkerGlobalScope;

export const CACHE_PREFIX = 'fleet-web';
export const ASSET_CACHE_NAME = `${CACHE_PREFIX}-assets-v1`;
export const API_CACHE_NAME = `${CACHE_PREFIX}-api-v1`;
const cacheableFileTypes = [
  'js',
  'css',
  'woff',
  'woff2',
  // 'svg',
  // 'png',
  // 'jpg',
  // 'jpeg',
  // 'gif',
];

const openCache = async (cacheName: string): Promise<Cache> => {
  return caches.open(cacheName);
};

const shouldHandleAssetRequest = (request: Request): boolean => {
  const isGetRequest = request.method === 'GET';
  const isFleetRequest = request.url.includes(location.origin);
  const isCorrectFileType = cacheableFileTypes.find((fileType) => request.url.endsWith(fileType));

  //Only cache requests to fleet frontend
  return Boolean(isGetRequest && isFleetRequest && isCorrectFileType);
};

/**
 * For now, only cache initial web launch related requests
 */
const shouldHandleApiRequest = (request: Request): boolean => {
  if (request.method !== 'GET') {
    return false;
  }

  // We do not serve cache responses from uptime requests with x-diam-group-id header
  if (request.headers.get('x-diam-group-id')) {
    return false;
  }

  const url = new URL(request.url);
  const path = url.pathname;
  const isCurrentUser = path.endsWith('/users/current');
  const isDashboard = path.includes('/dashboard/');
  const isDefaultAssetMap = path.endsWith('/assets/map') && url.search === '';
  return isCurrentUser || isDashboard || isDefaultAssetMap;
};

const getCachedRequest = async (
  cacheName: string,
  request: Request
): Promise<Response | undefined> => {
  const cache = await openCache(cacheName);
  return cache.match(request);
};

const isCacheableResponse = (response: Response): boolean => {
  if (!response.ok) {
    return false;
  }
  const cacheControl = response.headers.get('cache-control') || '';
  if (cacheControl.toLowerCase().includes('no-store')) {
    return false;
  }
  return true;
};

const fetchAndSaveToCache = async (cacheName: string, request: Request) => {
  const response = await fetch(request);
  if (isCacheableResponse(response)) {
    const cache = await openCache(cacheName);
    await cache.put(request, response.clone());
  }
  return response;
};

const broadcastAuthError = async (request: Request, status: number) => {
  const clients = await self.clients.matchAll({ includeUncontrolled: true, type: 'window' });
  for (const client of clients) {
    client.postMessage({ type: 'SW_AUTH_ERROR', status, url: request.url });
  }
};

/**
 * Requests the page explicitly wants fresh (e.g. `fetch(url, { cache: 'no-cache' })`) must not be
 * answered from the SW cache — otherwise a "refresh" right after launch gets the same stale entry
 * the initial request got, because that request's background revalidation hasn't landed yet.
 */
const isFreshRequest = (request: Request): boolean =>
  request.cache === 'no-cache' || request.cache === 'reload' || request.cache === 'no-store';

const isAuthErrorResponse = (response: Response): boolean =>
  response.status === 401 || response.status === 403;

/**
 * Network revalidations currently in flight, keyed by URL. A request arriving while one is in
 * flight waits for it instead of being served the entry that is about to be replaced.
 */
const inFlightRevalidations = new Map<string, Promise<Response>>();

const revalidate = (cache: Cache, request: Request): Promise<Response> => {
  const inFlight = inFlightRevalidations.get(request.url);
  if (inFlight) {
    return inFlight;
  }

  const revalidation = (async () => {
    // Bypass the HTTP cache too: requests join a revalidation by URL alone, so it must be fresh
    // enough for a joining no-cache request.
    const response = await fetch(request, { cache: 'no-cache' });
    if (isAuthErrorResponse(response)) {
      await cache.delete(request);
    } else if (isCacheableResponse(response)) {
      await cache.put(request, response.clone());
    }
    return response;
  })().finally(() => {
    inFlightRevalidations.delete(request.url);
  });

  inFlightRevalidations.set(request.url, revalidation);
  return revalidation;
};

const staleWhileRevalidateAuthAware = async (cacheName: string, event: FetchEvent) => {
  const cache = await openCache(cacheName);
  const inFlight = inFlightRevalidations.get(event.request.url);
  const cachedResponse = await cache.match(event.request);

  if (isFreshRequest(event.request) || (inFlight && cachedResponse)) {
    // Network-first (joining a revalidation already in flight); the result also refreshes the
    // cache for the next launch. The cache is only a fallback for network/server failures —
    // auth errors are returned as-is so the app can react to them.
    try {
      const response = await revalidate(cache, event.request);
      if (response.ok || isAuthErrorResponse(response) || !cachedResponse) {
        return response.clone();
      }
      return cachedResponse;
    } catch (error) {
      if (cachedResponse) {
        return cachedResponse;
      }
      throw error;
    }
  }

  if (cachedResponse) {
    const refreshPromise = (async () => {
      const response = await revalidate(cache, event.request);
      // If auth fails during revalidate, don't mask it forever.
      if (isAuthErrorResponse(response)) {
        await broadcastAuthError(event.request, response.status);
      }
    })().catch(() => undefined);

    event.waitUntil(refreshPromise);
    return cachedResponse;
  }

  // No cached response: behave normally so the app can see 401/403 and refresh.
  const response = await revalidate(cache, event.request);
  return response.clone();
};

const staleWhileRevalidate = async (cacheName: string, event: FetchEvent) => {
  const cachedResponse = await getCachedRequest(cacheName, event.request);
  if (cachedResponse) {
    const refreshPromise = fetchAndSaveToCache(cacheName, event.request).catch(() => undefined);
    event.waitUntil(refreshPromise);
    return cachedResponse;
  }
  return await fetchAndSaveToCache(cacheName, event.request);
};

self.addEventListener('fetch', (event) => {
  if (shouldHandleAssetRequest(event.request)) {
    event.respondWith(staleWhileRevalidate(ASSET_CACHE_NAME, event));
    return;
  }

  if (shouldHandleApiRequest(event.request)) {
    event.respondWith(staleWhileRevalidateAuthAware(API_CACHE_NAME, event));
  }
});

self.addEventListener('install', (event) => {
  console.log('Installing ServiceWorker');
  event.waitUntil(
    caches
      .open(ASSET_CACHE_NAME)
      .then(() => self.skipWaiting())
      .catch((error) => {
        console.error('Could not open cache in SW', error);
        throw error;
      })
  );
});

self.addEventListener('activate', (event) => {
  console.log('Activating SW. Removing old caches');
  event.waitUntil(
    caches
      .keys()
      .then((keys) =>
        Promise.all(
          keys
            .filter(
              (cacheName) =>
                cacheName.startsWith(`${CACHE_PREFIX}-`) &&
                cacheName !== ASSET_CACHE_NAME &&
                cacheName !== API_CACHE_NAME
            )
            .map((cacheName) => caches.delete(cacheName))
        )
      )
      .then(() => self.skipWaiting())
      .then(() => self.clients.claim())
  );
});
